import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { AlertShort, Loader } from '@sdir/sds';
import moment from 'moment';
import styled from 'styled-components';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { VesselPreviewLink } from '@sdir/blueprint';
// eslint-disable-next-line max-len
import { AdditionalCompetenceByPersonalIdDto } from '@sdir/httpclient/lib/clients/aps/additionalcompetence';
import {
  CompetenceRow,
  CompetencesHeader,
  CompetencesHeaderMobile,
  TableDefinition,
} from '../../../helpers/TableHeaders';
import { SortableDataTable } from '../../Molecules';
import { DisplayDateFormat } from '../../../helpers';
import { additionalCompetenceApi, vesselApi } from '../../../httpclient';

export const MyAdditionalCompetence: React.FC = () => {
  const intl = useIntl();
  const [isMobile, setIsMobile] = useState<boolean>(false);

  const {
    result: addtionalCompetences,
    error,
    loading,
  } = useApi({
    apiFunction: () => {
      const query = {
        take: 100,
        skip: 0,
      };
      return additionalCompetenceApi.v1AdditionalcompetenceapiGetbycurrentuserQueryPost(query);
    },
    callApiOnInit: true,
  });

  const table = useMemo<TableDefinition<CompetenceRow> | null>(() => {
    if (!addtionalCompetences) return null;

    const loadVesselForAdditionalComptence = (competence: AdditionalCompetenceByPersonalIdDto) => {
      if (!competence.vesselId)
        return Promise.reject(
          new Error(intl.formatMessage({ id: 'vessel.preview.errors.missingvesselid' }))
        );
      return vesselApi
        .v1VesselSearchfromrouteUidGet(competence.vesselId)
        .then((r) => r.data?.source?.[0] || {});
    };

    const tableData = addtionalCompetences.map((e) => ({
      vessel: e.vesselId ? (
        <VesselPreviewLink
          name={e.vessel || ''}
          loadAction={() => loadVesselForAdditionalComptence(e)}
        />
      ) : (
        e.vessel || ''
      ),
      vesselValue: e.vessel || '',
      additionalCompetende: e.competenceDescription || '',
      period: `${e.periodStartDate ? DisplayDateFormat(e.periodStartDate) : ''} - ${
        e.periodStopDate ? DisplayDateFormat(e.periodStopDate) : ''
      }`,
      periodStart: moment(e.periodStartDate),
      periodEnd: moment(e.periodStopDate),
      childContent: isMobile ? <RowMobileContent rowData={e} fm={intl.formatMessage} /> : null,
    }));

    if (isMobile) {
      return {
        columns: CompetencesHeaderMobile(intl.formatMessage),
        data: tableData,
        sortableKeys: ['vessel'],
        collapsable: true,
        collapsableText: [
          intl.formatMessage({ id: 'general.table.viewdetails' }),
          intl.formatMessage({ id: 'general.table.hidedetails' }),
        ],
      };
    }

    return {
      columns: CompetencesHeader(intl.formatMessage),
      data: tableData,
      sortableKeys: '*',
    };
  }, [addtionalCompetences, intl, isMobile]);

  const showEmpty = !addtionalCompetences || addtionalCompetences.length === 0;

  useEffect(() => {
    function handleResize() {
      const isMobileScreen = !!(window?.innerWidth < 768);
      setIsMobile(isMobileScreen);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Wrapper>
      <h2>{intl.formatMessage({ id: 'home.tile.competence' })}</h2>
      {loading && <Loader />}
      {error && <ErrorMessage error={error} />}
      {showEmpty && !error && (
        <AlertShort text={intl.formatMessage({ id: 'competence.notfound' })} type="info" />
      )}
      {table && !showEmpty && (
        <TableWrapper data-testid="competence-table">
          <StyledSortableDataTable table={table} autoWidth />
        </TableWrapper>
      )}
    </Wrapper>
  );
};

interface RowMobileContentProps {
  rowData: AdditionalCompetenceByPersonalIdDto;
  fm: any;
}

const RowMobileContent: React.FC<RowMobileContentProps> = ({ rowData, fm }) => {
  return (
    <>
      <MobileContentItem>
        <SubHeader>{fm({ id: 'common.table.periode' })}</SubHeader>
        <SubValue>
          {`${rowData.periodStartDate ? DisplayDateFormat(rowData.periodStartDate) : ''} - ${
            rowData.periodStopDate ? DisplayDateFormat(rowData.periodStopDate) : ''
          }`}
        </SubValue>
      </MobileContentItem>
      <MobileContentItem>
        <SubHeader>{fm({ id: 'competence.table.subheader3' })}</SubHeader>
        <SubValue>{rowData.competenceDescription || ''}</SubValue>
      </MobileContentItem>
    </>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
`;

const TableWrapper = styled.div`
  max-width: 100%;
`;

const StyledSortableDataTable = styled(SortableDataTable)`
  margin-left: -5px;
  padding: 0 0 20px 0;
  background-color: transparent;
  thead tr th {
    padding-left: 0;
    button {
      text-align: left;
    }
  }
  tbody tr {
    border-top: 1px solid ${({ theme }) => theme.colors.primary8};
    min-height: 40px;

    &:last-child {
      border-bottom: 1px solid ${({ theme }) => theme.colors.primary8};
    }
  }
`;

const MobileContentItem = styled.div`
  margin-bottom: 10px;
`;

const SubHeader = styled.span`
  display: block;
  font-weight: bold;
  margin-bottom: 2px;
`;

const SubValue = styled.span`
  display: block;
`;
